<script lang="ts">
  // App shell — the router host with the PR 2 auth gate.
  //
  // Boots by calling `getSession()`. Three terminal states:
  //   - `unknown`         → boot splash (1 frame; transient).
  //   - `unauthenticated` → mount <PageLogin /> standalone.
  //   - `authenticated`   → mount the full router shell (Sidebar +
  //                          Topbar + page + Overlays) + start WS.
  //
  // PR 2 of `auth-via-telegram-otp`; spec scenarios #28-#32.
  //
  // Session-expired mid-use is handled by the auth interceptor in
  // `lib/auth.ts` (installAuthInterceptor). When an `/api/*` returns
  // 401 while authenticated, the interceptor flips authState to
  // `unauthenticated`, which causes App to swap the shell out for
  // `<PageLogin />` automatically.

  import { onDestroy, onMount } from "svelte";
  import Sidebar from "./components/Sidebar.svelte";
  import Topbar from "./components/Topbar.svelte";
  import Overlays from "./components/Overlays.svelte";
  import PageOverview from "./components/PageOverview.svelte";
  import PageHost from "./components/PageHost.svelte";
  import PageContainers from "./components/PageContainers.svelte";
  import PageContainerDetail from "./components/PageContainerDetail.svelte";
  import PageAudit from "./components/PageAudit.svelte";
  import PageLogin from "./pages/PageLogin.svelte";
  import {
    resolveRoute,
    subscribeToRoute,
    replace,
    type RouteMatch,
  } from "./lib/router";
  import {
    applyAuth,
    authState,
    authUser,
    getSession,
    installAuthInterceptor,
    getValue,
  } from "./lib/auth";
  import { createWsClient, type WsClient, type ConnectionState } from "./lib/ws";
  import { mountStaleOverlay, dismissStaleOverlay } from "./lib/overlays";

  // Sync the initial route from the URL so the SPA lands on the right
  // page even before onMount runs (test-environment parity — tests
  // inspect the DOM synchronously after render).
  let route: RouteMatch = resolveRoute(
    typeof window === "undefined"
      ? "/"
      : window.location.pathname + window.location.search,
  );

  // PR 2: read auth state synchronously from the mocked authState
  // store. In production the mock is the real module; the initial
  // value is `'unknown'` until `getSession()` resolves.
  let currentState: ReturnType<typeof getValue<typeof authState>> =
    typeof window === "undefined"
      ? "unknown"
      : getValue(authState);

  let unsubscribeRoute: (() => void) | null = null;
  let ws: WsClient | null = null;
  let staleTimer: ReturnType<typeof setInterval> | null = null;
  let lastConnected = false;
  let staleAt: number | null = null;

  const STALE_THRESHOLD_MS = 10_000;

  // Reactive: whenever authState flips, decide which subtree to mount.
  // On a flip from 'unauthenticated' -> 'authenticated', read `?next=`
  // off the URL and push the user back to the page they were trying
  // to reach (scenario #32).
  let authUnsub: (() => void) | null = null;
  let userUnsub: (() => void) | null = null;

  function nextRedirectTarget(): string {
    if (typeof window === "undefined") return "/";
    const sp = new URLSearchParams(window.location.search);
    const next = sp.get("next");
    return next && next.startsWith("/") ? next : "/";
  }

  function isPublicPath(path: string): boolean {
    return path === "/login" || path.startsWith("/login?");
  }

  function onAuthStateChange(next: "unknown" | "unauthenticated" | "authenticated"): void {
    currentState = next;
    // Public-path gate: if we're on /login and just became authenticated,
    // redirect to ?next || "/" (scenario #32).
    if (next === "authenticated" && isPublicPath(window.location.pathname)) {
      const target = nextRedirectTarget();
      // Use replace() so the back button doesn't bounce the user back to /login.
      if (window.location.pathname + window.location.search !== target) {
        replace(target);
      }
      startWs();
      return;
    }
    // Reverse direction (authenticated → unauthenticated): if the user
    // isn't already on /login, append `?next=<currentPath>` so they can
    // be sent back after re-auth. This handles "session expired mid-use"
    // (scenario #30) as well as deep-link / unauthenticated mounts
    // (scenario #28).
    if (next === "unauthenticated" && !isPublicPath(window.location.pathname)) {
      const target = `/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`;
      if (window.location.pathname + window.location.search !== target) {
        replace(target);
      }
      return;
    }
    if (next === "authenticated") {
      // Landed on a non-/login authenticated state (boot probe hit
      // first, or session recovered after an expiry). Start the WS
      // client so the StaleOverlay path is wired even before the
      // bootSession async chain resolves.
      startWs();
    }
  }

  // Install the global 401 interceptor exactly once (idempotent).
  installAuthInterceptor();

  // Subscribe BEFORE we boot the session probe so a same-tick flip is
  // observed.
  authUnsub = authState.subscribe(onAuthStateChange);
  userUnsub = authUser.subscribe(() => {
    /* reserved for future derived state — keep subscription alive */
  });

  onMount(() => {
    unsubscribeRoute = subscribeToRoute((next) => {
      route = next;
      // If a route change pushes us OUT of /login while unauthenticated,
    // bounce back. This catches "/" → "/host" navigation that started
      // during the boot window.
      if (
        currentState !== "authenticated" &&
        !isPublicPath(window.location.pathname)
      ) {
        const target = `/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`;
        if (window.location.pathname + window.location.search !== target) {
          replace(target);
        }
      }
    });

    staleTimer = setInterval(() => {
      if (staleAt === null) return;
      if (Date.now() - staleAt >= STALE_THRESHOLD_MS) {
        mountStaleOverlay();
      }
    }, 1000);

    // Boot: probe the session. The shell MUST NOT mount until we know
    // whether the cookie is valid — otherwise we'd flash the UI then
    // yank it away (R-SPA-AuthGate scenario #28).
    void bootSession();
  });

  async function bootSession(): Promise<void> {
    // Idempotent — the auth-state subscription flips state immediately
    // for the common case (already-have-session / known-no-session),
    // and we use this chain only for the rare "no auth state cookie
    // AND the agent happens to send a Set-Cookie before the probe"
    // race. startWs() lives in the auth-state subscriber so it fires
    // synchronously when the state flips to "authenticated".
    if (currentState !== "unknown") return;
    try {
      const session = await getSession();
      if (session === null) {
        applyAuth({ state: "unauthenticated", user: null });
      } else {
        applyAuth({
          state: "authenticated",
          user: { user: session.user, csrf: session.csrf },
        });
      }
    } catch {
      applyAuth({ state: "unauthenticated", user: null });
    }
  }

  function startWs(): void {
    if (ws || typeof window === "undefined") return;
    ws = createWsClient();
    ws.connect();
    ws.connectionState.subscribe((next: ConnectionState) => {
      if (next === "connected") {
        lastConnected = true;
        staleAt = null;
        dismissStaleOverlay();
      } else if (lastConnected) {
        if (staleAt === null) staleAt = Date.now();
      } else {
        mountStaleOverlay();
      }
    });
  }

  onDestroy(() => {
    unsubscribeRoute?.();
    authUnsub?.();
    userUnsub?.();
    ws?.close();
    if (staleTimer !== null) clearInterval(staleTimer);
  });
</script>

{#if currentState === "authenticated"}
  <div class="app-shell">
    <Sidebar />
    <div class="app-shell__main">
      <Topbar />
      <main class="app-shell__content" data-route={route.name}>
        {#if route.name === "overview"}
          <PageOverview />
        {:else if route.name === "host"}
          <PageHost />
        {:else if route.name === "containers"}
          <PageContainers />
        {:else if route.name === "container-detail"}
          <PageContainerDetail route={route} />
        {:else if route.name === "audit"}
          <PageAudit />
        {:else if route.name === "login"}
          <!-- While authenticated we keep `/login` reachable as a
           reload-to-root hint; redirect kicks in via the auth
           subscription's `next`-resolution. -->
          <PageLogin />
        {:else}
          <div class="app-shell__placeholder" data-testid="route-placeholder">
            <span>Unknown route</span>
          </div>
        {/if}
      </main>
    </div>
    <Overlays />
  </div>
{:else if currentState === "unauthenticated"}
  <PageLogin />
{:else}
  <!-- `unknown` — single-frame splash so the page doesn't paint a
       flash of "no content" before the session probe resolves. -->
  <div class="app-bootstrap" data-testid="app-bootstrap" aria-hidden="true"></div>
{/if}

<style>
  .app-bootstrap {
    min-height: 100vh;
    background: var(--bg);
  }
</style>
